import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/components/ui/Toast'
import { ClientError } from 'graphql-request'
import { invalidateListQuery } from '@/lib/query_invalidate'

export interface UseMutationWithToastOptions<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>
  successMessage?: string
  errorMessage?: string
  invalidateKeys?: string[][]
  onSuccess?: (data: TData) => void
  onMutate?: (variables: TVariables) => Promise<unknown> | unknown
  onError?: (error: Error, variables: TVariables, context: unknown) => void
}

export function useMutationWithToast<TData, TVariables>({
  mutationFn,
  successMessage = 'Operation completed successfully',
  errorMessage = 'An error occurred',
  invalidateKeys = [],
  onSuccess,
  onMutate,
  onError: onErrorCallback,
}: UseMutationWithToastOptions<TData, TVariables>) {
  const queryClient = useQueryClient()

  return useMutation<TData, Error, TVariables, unknown>({
    mutationFn,
    onMutate,
    onSuccess: (data) => {
      toast.success(successMessage)

      for (const key of invalidateKeys) {
        void invalidateListQuery(queryClient, key)
      }

      onSuccess?.(data)
    },
    onError: (error: Error, variables: TVariables, context: unknown) => {
      // Extract clean GraphQL error message from graphql-request ClientError
      const gqlErrors = (error instanceof ClientError) ? error.response?.errors : undefined;
      const gqlMessage = gqlErrors?.[0]?.message;
      toast.error(gqlMessage || error.message || errorMessage)
      onErrorCallback?.(error, variables, context)
    },
  })
}
